/**
 * NativelyPurchases Integration Utility
 * 
 * Handles RevenueCat in-app purchases through the NativelyPurchases SDK
 * for iOS native app purchases.
 */

import { isNativeApp } from "./platform";
import { formatCurrency } from "./utils";

// RevenueCat Package IDs
export const RC_PACKAGE_IDS = {
  MONTHLY: "$rc_monthly",
  ANNUAL: "$rc_annual",
  LIFETIME: "$rc_lifetime",
  CREDITS: "Credits",
} as const;

// Apple IAP Pricing in USD (with markup)
export const APPLE_PRICING_USD = {
  MONTHLY: 5.99,
  ANNUAL: 34.99,
  LIFETIME: 99.99,
  CREDITS: 4.99,
} as const;

// Stripe Web Pricing in USD (original)
export const WEB_PRICING_USD = {
  MONTHLY: 3.99,
  ANNUAL: 20.11,
  LIFETIME: 99.00,
  CREDITS: 3.00,
} as const;

// Legacy string pricing for backwards compatibility
// @deprecated Use APPLE_PRICING_USD and formatCurrency instead
export const APPLE_PRICING = {
  MONTHLY: "$5.99",
  ANNUAL: "$34.99",
  LIFETIME: "$99.99",
  CREDITS: "$4.99",
} as const;

// Legacy string pricing for backwards compatibility
// @deprecated Use WEB_PRICING_USD and formatCurrency instead
export const WEB_PRICING = {
  MONTHLY: "$3.99",
  ANNUAL: "$20.11",
  LIFETIME: "$99.00",
  CREDITS: "$3.00",
} as const;

export type PurchaseType = "monthly" | "annual" | "lifetime" | "credits";

/**
 * Check if NativelyPurchases SDK is available
 */
export const isPurchasesAvailable = (): boolean => {
  return isNativeApp() && typeof window !== 'undefined' && !!window.NativelyPurchases;
};

/**
 * Initialize NativelyPurchases instance
 */
export const initializePurchases = (): any | null => {
  if (!isPurchasesAvailable()) {
    console.log('[Purchases] NativelyPurchases SDK not available');
    return null;
  }

  try {
    const purchases = new window.NativelyPurchases!();
    console.log('[Purchases] NativelyPurchases initialized');
    return purchases;
  } catch (error) {
    console.error('[Purchases] Failed to initialize NativelyPurchases:', error);
    return null;
  }
};

/**
 * Get current subscriber info from device (if any)
 */
const getCurrentSubscriberInfo = async (purchases: any): Promise<{
  subscriberId: string | null;
  originalTransactionId: string | null;
} | null> => {
  return new Promise((resolve) => {
    try {
      purchases.getCustomerInfo((response: any) => {
        if (response.status === 'SUCCESS' && response.customerId) {
          resolve({
            subscriberId: response.customerId,
            originalTransactionId: response.originalTransactionId || null
          });
        } else {
          resolve(null);
        }
      });
    } catch (error) {
      console.error('[Purchases] Error getting customer info:', error);
      resolve(null);
    }
  });
};

/**
 * Login user to RevenueCat with security validation
 * 
 * SECURITY: Validates ownership before allowing RevenueCat SDK login
 * to prevent account transfer attacks
 */
export const loginToRevenueCat = async (
  userId: string,
  userEmail: string,
  supabase: any
): Promise<{ success: boolean; customerId?: string; error?: string }> => {
  const purchases = initializePurchases();
  if (!purchases) {
    return { success: false, error: "NativelyPurchases SDK not available" };
  }

  // SECURITY: Check ownership BEFORE calling RevenueCat SDK
  // Get current subscriber ID from device (if any)
  const currentSubscriberInfo = await getCurrentSubscriberInfo(purchases);
  
  if (currentSubscriberInfo?.subscriberId) {
    console.log('[Purchases] Validating subscriber ownership:', currentSubscriberInfo.subscriberId);
    
    // Check if we're allowed to use this subscriber ID
    try {
      const { data, error } = await supabase.functions.invoke('revenuecat-login', {
        body: {
          revenuecat_subscriber_id: currentSubscriberInfo.subscriberId,
          original_transaction_id: currentSubscriberInfo.originalTransactionId
        }
      });

      if (error || !data?.can_login) {
        console.error('[Purchases] Login blocked:', data?.message || error);
        return {
          success: false,
          error: data?.message || 'This subscription belongs to another account. Subscriptions cannot be transferred.'
        };
      }
      
      console.log('[Purchases] Ownership validated, proceeding with login');
    } catch (validationError) {
      console.error('[Purchases] Validation error:', validationError);
      return {
        success: false,
        error: 'Failed to validate subscription ownership'
      };
    }
  }

  // Ownership validated or no existing subscription - proceed with RevenueCat SDK login
  return new Promise((resolve) => {
    try {
      purchases.login(userId, userEmail, (response: any) => {
        console.log('[Purchases] Login response:', response);
        
        if (response.status === 'SUCCESS') {
          resolve({
            success: true,
            customerId: response.customerId,
          });
        } else {
          resolve({
            success: false,
            error: response.error || "Login failed",
          });
        }
      });
    } catch (error) {
      console.error('[Purchases] Login error:', error);
      resolve({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  });
};

/**
 * Logout user from RevenueCat
 */
export const logoutFromRevenueCat = async (): Promise<{ success: boolean; error?: string }> => {
  const purchases = initializePurchases();
  if (!purchases) {
    return { success: false, error: "NativelyPurchases SDK not available" };
  }

  return new Promise((resolve) => {
    try {
      purchases.logout((response: any) => {
        console.log('[Purchases] Logout response:', response);
        resolve({ success: response.status === 'SUCCESS' });
      });
    } catch (error) {
      console.error('[Purchases] Logout error:', error);
      resolve({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  });
};

/**
 * Get package price from RevenueCat
 */
export const getPackagePrice = async (
  packageId: string
): Promise<{ success: boolean; price?: string; localized?: string; error?: string }> => {
  const purchases = initializePurchases();
  if (!purchases) {
    return { success: false, error: "NativelyPurchases SDK not available" };
  }

  return new Promise((resolve) => {
    try {
      purchases.packagePrice(packageId, (response: any) => {
        console.log('[Purchases] Price response:', response);
        
        if (response.status === 'SUCCESS') {
          resolve({
            success: true,
            price: response.price,
            localized: response.localized,
          });
        } else {
          resolve({
            success: false,
            error: response.error || "Failed to get price",
          });
        }
      });
    } catch (error) {
      console.error('[Purchases] Get price error:', error);
      resolve({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  });
};

/**
 * Purchase a package through Apple IAP
 */
export const purchasePackage = async (
  packageId: string
): Promise<{ success: boolean; transactionId?: string; error?: string }> => {
  const purchases = initializePurchases();
  if (!purchases) {
    return { success: false, error: "NativelyPurchases SDK not available" };
  }

  return new Promise((resolve) => {
    try {
      purchases.purchasePackage(packageId, (response: any) => {
        console.log('[Purchases] Purchase response:', response);
        console.log('[Purchases] Purchase response FULL:', JSON.stringify(response, null, 2));
        console.log('[Purchases] Response status:', response.status);
        console.log('[Purchases] Response error:', response.error);
        console.log('[Purchases] Response keys:', Object.keys(response || {}));
        
        if (response.status === 'SUCCESS') {
          resolve({ 
            success: true,
            transactionId: response.transactionId || response.transaction_id
          });
        } else {
          // Log detailed failure information
          console.error('[Purchases] Purchase FAILED - Status:', response.status);
          console.error('[Purchases] Purchase FAILED - Error:', response.error);
          console.error('[Purchases] Purchase FAILED - Full response:', JSON.stringify(response, null, 2));
          
          // Parse error message to provide helpful context
          let errorMessage = response.error || response.message || `Purchase failed with status: ${response.status}`;
          
          // Check for common error patterns and provide user-friendly messages
          const errorLower = (errorMessage || '').toLowerCase();
          if (errorLower.includes('not eligible') || errorLower.includes('trial')) {
            errorMessage = "You've already used the free trial for this subscription. Please select the regular subscription option without the trial.";
          } else if (errorLower.includes('already subscribed') || errorLower.includes('existing subscription')) {
            errorMessage = "You already have an active subscription. Try using the 'Restore Purchases' button instead.";
          } else if (errorLower.includes('cancelled') || errorLower.includes('user cancelled')) {
            errorMessage = "Purchase was cancelled. Please try again if you'd like to subscribe.";
          } else if (errorLower.includes('network') || errorLower.includes('connection')) {
            errorMessage = "Network error. Please check your internet connection and try again.";
          } else if (errorLower.includes('product') || errorLower.includes('not found')) {
            errorMessage = "Subscription product not found. Please contact support.";
          }
          
          resolve({
            success: false,
            error: errorMessage,
          });
        }
      });
    } catch (error) {
      console.error('[Purchases] Purchase error:', error);
      resolve({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  });
};

/**
 * Restore purchases
 */
export const restorePurchases = async (): Promise<{
  success: boolean;
  customerId?: string;
  error?: string;
}> => {
  const purchases = initializePurchases();
  if (!purchases) {
    return { success: false, error: "NativelyPurchases SDK not available" };
  }

  return new Promise((resolve) => {
    try {
      purchases.restore((response: any) => {
        console.log('[Purchases] Restore response:', response);
        
        if (response.status === 'SUCCESS') {
          resolve({
            success: true,
            customerId: response.customerId,
          });
        } else {
          resolve({
            success: false,
            error: response.error || "Restore failed",
          });
        }
      });
    } catch (error) {
      console.error('[Purchases] Restore error:', error);
      resolve({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  });
};

/**
 * Get the package ID for a purchase type
 */
export const getPackageIdForType = (type: PurchaseType): string | null => {
  switch (type) {
    case "monthly":
      return RC_PACKAGE_IDS.MONTHLY;
    case "annual":
      return RC_PACKAGE_IDS.ANNUAL;
    case "lifetime":
      return RC_PACKAGE_IDS.LIFETIME;
    case "credits":
      // Credits are one-time purchases, not subscriptions
      // We'll need to handle this differently
      return null;
    default:
      return null;
  }
};

/**
 * Get Apple pricing for a purchase type (localized with exchange rate conversion)
 */
export const getApplePricing = (type: PurchaseType): string => {
  switch (type) {
    case "monthly":
      return formatCurrency(APPLE_PRICING_USD.MONTHLY);
    case "annual":
      return formatCurrency(APPLE_PRICING_USD.ANNUAL);
    case "lifetime":
      return formatCurrency(APPLE_PRICING_USD.LIFETIME);
    case "credits":
      return formatCurrency(APPLE_PRICING_USD.CREDITS);
    default:
      return "";
  }
};

/**
 * Get web pricing for a purchase type (localized with exchange rate conversion)
 */
export const getWebPricing = (type: PurchaseType): string => {
  switch (type) {
    case "monthly":
      return formatCurrency(WEB_PRICING_USD.MONTHLY);
    case "annual":
      return formatCurrency(WEB_PRICING_USD.ANNUAL);
    case "lifetime":
      return formatCurrency(WEB_PRICING_USD.LIFETIME);
    case "credits":
      return formatCurrency(WEB_PRICING_USD.CREDITS);
    default:
      return "";
  }
};
